import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { tokenStore } from '@/features/auth/tokenStore';
import { config } from '@/lib/config';
import { normalizeError } from '@/lib/errors';

/**
 * A request-correlation id. `crypto.randomUUID()` only exists in a secure
 * context (HTTPS or localhost); when the app is served over plain HTTP on a
 * public host it is `undefined`, so calling it would throw inside the request
 * interceptor and surface as a generic "unexpected error". Fall back to a
 * non-cryptographic id in that case.
 */
function newRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // insecure context — fall through to the manual id
  }
  return `rid-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * The single axios instance for talking to the Laravel control plane. The
 * browser never calls the Python AI service or OpenMetadata directly — those
 * are internal and reached only through Laravel.
 *
 * Interceptors:
 *   - request:  attach bearer token + a per-request X-Request-Id for tracing
 *               that correlates with backend logs.
 *   - response: reject with a normalized ApiError so every caller sees the same
 *               error type.
 */
function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: config.apiBaseUrl,
    // LLM-backed endpoints (catalogue enrichment) can take longer than a
    // typical request; 60s gives them headroom.
    timeout: 60_000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    const token = tokenStore.get();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    request.headers.set('X-Request-Id', newRequestId());
    // Let the browser set multipart boundaries for file uploads.
    if (request.data instanceof FormData) {
      request.headers.delete('Content-Type');
    }
    return request;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: unknown) => Promise.reject(normalizeError(error)),
  );

  return instance;
}

export const apiClient = createApiClient();

/**
 * Thin typed GET helper used by feature query functions.
 */
export async function getJson<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<T>(path, { params });
  return response.data;
}

/**
 * Thin typed POST helper.
 */
export async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const response = await apiClient.post<T>(path, body);
  return response.data;
}

/**
 * Thin typed PATCH helper.
 */
export async function patchJson<T>(path: string, body?: unknown): Promise<T> {
  const response = await apiClient.patch<T>(path, body);
  return response.data;
}
